import { Get, Put } from "@configs/Axios";
import {
  DetectionCondition,
  EventStatus,
  EventType,
  TemperatureType,
  Union,
  VehicleType,
  WidgetItem,
  WidgetListItem
} from "@custom-types/Types";

export const EventWidgetType = {
  ALL: "all",
  SLIP_AND_FALL: "slipAndFall",
  SHOPPING_CART_LOSS: "shoppingCartLoss",
  TRAFFIC_JAM: "trafficJam",
  MES: "mes",
  TEMPERATURE: "temperature",
  STOPPED_VEHICLE: "stoppedVehicle",
  FORKLIFT_SPEED: "forkliftSpeed"
} as const;

export type EventRequestWidgetType = Union<typeof EventWidgetType>;

export type ChangeLog = {
  status: EventStatus;
  timestamp: string;
};

export type GetEventsRequest = {
  widgetId?: string;
  page?: number;
  limit?: number;
  startDateTime?: string;
  endDateTime?: string;
  types?: EventRequestWidgetType[];
  slipAndFall?: EventStatus[];
  trafficJam?: EventStatus[];
  mes?: EventStatus[];
  temperature?: EventStatus[];
  stoppedVehicle?: EventStatus[];
  vehicleType?: string;
};

export type UpdateEventRequest = {
  id: string;
  status: EventStatus;
};

export type SlipAndFallEventDataType = null;

export type EventManagementEventDataType = {
  vehicleType?: VehicleType;
  durationTime?: number;
  equipmentId?: string;
  alarmId?: string;
};

type TrafficJamEventDataType = null;

export type MesManagementDataType = {
  durationTime: number;
  equipmentId: string;
  alarmId: string;
};

export type TemperatureDataType = {
  temperatureType: TemperatureType;
  detectionCondition: DetectionCondition;
};

export type StoppedVehicleDataType = { vehicleType: VehicleType };

export type ForkliftEventDataType = null;

export type EventDataType =
  | MesManagementDataType
  | TemperatureDataType
  | StoppedVehicleDataType
  | SlipAndFallEventDataType
  | TrafficJamEventDataType
  | EventManagementEventDataType;

export interface GetEventResponse<T> {
  id: string;
  type: EventType;
  device: {
    name: string;
    id: string;
    channel: number;
    ruleName: string;
    model: string;
    locationName: string;
  };
  widgets: WidgetListItem[];
  status: EventStatus;
  timestamp: string;
  thumbnail: string;
  changeLogs: ChangeLog[];
  data?: T;
  widget: WidgetItem;
}

export interface GetEventsResponse<T> {
  total: number;
  page: number;
  limit: number;
  data: GetEventResponse<T>[];
}

interface GetEventsTotalCountResponse {
  total: number;
}

export type UpdateEventResponse = {
  id: string;
  status: EventStatus;
  timestamp: string;
  changeLogs: [
    {
      status: EventStatus;
      timestamp: string;
    }
  ];
};

export const eventsApis = {
  getEvents: async (orgId: string, req: GetEventsRequest) => {
    if (!orgId || (req && req.types && req.types.length < 1))
      return { data: [] };
    return Get<GetEventsResponse<EventDataType>>(
      `/v3/organizations/${orgId}/events`,
      {
        params: req
      }
    );
  },
  getEventsTotalCount: async (orgId: string, req: GetEventsRequest) => {
    if (!orgId || (req && req.types && req.types.length < 1))
      return { total: 0 };
    return Get<GetEventsTotalCountResponse>(
      `/v2/organizations/${orgId}/events/total`,
      {
        params: req
      }
    );
  },
  updateEvent: async (orgId: string, { id, ...req }: UpdateEventRequest) => {
    return Put<UpdateEventResponse>(
      `/v2/organizations/${orgId}/events/${id}`,
      req
    );
  }
};
